<template>
  <div class="about-page">
    <v-container class="about-container">
      <!-- Hero Section -->
      <div class="about-hero">
        <h1 class="about-title">What's My Story?</h1>
        <p class="about-subtitle">
          Bridge between engineering, docs, and users
        </p>
      </div>

      <!-- Main Content -->
      <v-row class="content-section">
        <!-- Image Column -->
        <v-col cols="12" md="4" class="image-column">
          <div class="image-wrapper">
            <img
              alt="Aisha's Tech Room - Technical Writer & Software Developer"
              src="../assets/aisha.png"
              class="profile-pic-about-me"
            />
          </div>
        </v-col>

        <!-- Text Column -->
        <v-col cols="12" md="8" class="text-column">
          <div class="story-content">
            <div class="intro-section">
              <h2>Hello! I'm Aisha 👋</h2>
              <p>
                I work at a global technology company, where I focus on software
                development workflow and technical communication. Although I
                started my career as a Frontend Developer, I still code
                regularly because I genuinely enjoy solving problems. Aside from
                my corporate life, I am a social entrepreneur, a YouTuber, and a
                Podcaster.
              </p>
            </div>

            <div class="journey-section">
              <h2>My Journey</h2>
              <p>
                My journey in technology began almost a decade ago. It truly
                started with my first job here in Sweden as a Content Developer,
                where I was deeply exposed to building websites with WordPress.
                After a brief time working in Social Science, my original
                educational background, I realized I wasn't happy in that field.
                I resigned and completed an intensive coding bootcamp to become
                a Frontend Developer.
              </p>
            </div>

            <v-expand-transition>
              <div v-show="showFullStory" class="expanded-content">
                <div class="breakthrough-section">
                  <h2>The Breakthrough</h2>
                  <p>
                    The best decision I ever made was enrolling in formal
                    Computer Science courses. I felt like I was missing
                    something without a formal CS degree. It confirmed that the
                    process is the same as any other education: you learn the
                    materials, you practice, and you're tested on your
                    knowledge. I even earned an 'A' on one of my first C# exams!
                  </p>
                </div>

                <div class="multifaceted-section">
                  <h2>Embracing My Multifaceted Identity</h2>
                  <p>
                    As you can see, I am a very multifaceted person, and I own
                    that. Many people feel they need to specialize in just one
                    area, and I used to be one of them. Now I know that in life,
                    you can pursue many passions. This is why I proudly hold
                    education in both Social Science and Computer Science
                    coursework.
                  </p>
                </div>

                <div class="wemotion-section">
                  <h2>WEMotion: My Social Enterprise</h2>
                  <p>
                    Aside from my corporate work, I founded
                    <strong>WEMotion</strong>, a social enterprise dedicated to
                    helping underprivileged girls in Africa to stay in school
                    through technology and mentorship, the website is down for a
                    rebuild.
                  </p>
                </div>

                <div class="mission-section">
                  <h2>My Mission</h2>
                  <p>
                    I want my legacy to reshape the narrative around working in
                    tech. It is not as exclusive as it may seem. Anyone with the
                    aspiration to become a developer or work in tech can do it,
                    regardless of their background, country of origin, race,
                    ethnicity, language or gender.
                    <strong>If I can do it, you absolutely can, too.</strong>
                  </p>
                </div>

                <div class="real-talk-section">
                  <blockquote>
                    My story isn't all sweet tales,it has been full of
                    challenges and it wasn't an easy ride to get to where I am
                    today. I plan to share all of it so you can learn from my
                    experiences and, hopefully, avoid some of the same
                    challenges.
                  </blockquote>
                </div>

                <div class="closing-section">
                  <p class="thank-you">
                    Thank you for stopping by. It's going to be a wonderful
                    ride. ✨
                  </p>
                </div>
              </div>
            </v-expand-transition>

            <v-btn
              variant="text"
              color="#00bcd4"
              @click="showFullStory = !showFullStory"
              class="read-more-btn"
              size="large"
            >
              {{ showFullStory ? "Read Less ▲" : "Read More ▼" }}
            </v-btn>

            <!-- CTA Section -->
            <div class="cta-section">
              <h3>Let's Connect!</h3>
              <p>
                Follow my journey and let's build something amazing together.
              </p>
              <div class="cta-buttons">
                <v-btn
                  color="#0077B5"
                  variant="elevated"
                  size="large"
                  href="https://www.linkedin.com/in/aisha-keita/"
                  target="_blank"
                  prepend-icon="mdi-linkedin"
                >
                  Connect on LinkedIn
                </v-btn>
                <v-btn
                  to="/blog"
                  color="#00bcd4"
                  variant="outlined"
                  size="large"
                  prepend-icon="mdi-book-open-variant"
                >
                  Read My Blog
                </v-btn>
              </div>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script setup>
import { ref } from "vue";
const showFullStory = ref(false);
</script>

<style scoped>
.about-page {
  background: #ffffff;
  min-height: 100vh;
  padding: 3rem 0;
}

.about-container {
  max-width: 1100px;
}

.about-hero {
  text-align: center;
  margin-bottom: 4rem;
  animation: fadeInDown 0.8s ease;
}

.about-title {
  font-size: clamp(2.2rem, 5vw, 3.2rem);
  font-weight: 800;
  margin-bottom: 0.75rem;
  background: linear-gradient(135deg, #00bcd4 0%, #0097a7 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.about-subtitle {
  font-size: 1.1rem;
  color: #64748b;
  font-weight: 500;
}

.content-section {
  animation: fadeInUp 1s ease;
}

.image-column {
  position: relative;
}

.image-wrapper {
  position: sticky;
  top: 2rem;
  text-align: center;
}

.profile-pic-about-me {
  width: 100%;
  max-width: 320px;
  height: auto;
  border-radius: 16px;
  box-shadow: 0 16px 48px rgba(0, 188, 212, 0.15);
  border: 3px solid #e0f7fa;
  transition: transform 0.3s ease;
  margin-bottom: 1.5rem;
}

.profile-pic-about-me:hover {
  transform: scale(1.03);
}

.text-column {
  padding-left: 2rem;
}

.story-content h2 {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  margin-top: 2rem;
  margin-bottom: 0.75rem;
  border-left: 4px solid #00bcd4;
  padding-left: 0.875rem;
}

.story-content p {
  font-size: 1.05rem;
  line-height: 1.85;
  color: #475569;
  margin-bottom: 1.25rem;
}

.intro-section {
  margin-bottom: 1.5rem;
}

.intro-section h2 {
  border-left: none;
  padding-left: 0;
  font-size: 1.75rem;
}

.journey-section,
.breakthrough-section,
.multifaceted-section,
.wemotion-section,
.mission-section {
  margin-bottom: 1.5rem;
}

.real-talk-section {
  margin: 2rem 0;
}

.real-talk-section blockquote {
  padding: 1.5rem 1.75rem;
  background: #f0f9ff;
  border-left: 4px solid #00bcd4;
  border-radius: 8px;
  font-style: italic;
  color: #1e3a5f;
  font-size: 1.05rem;
  line-height: 1.8;
}

.closing-section {
  text-align: center;
  margin: 2rem 0;
}

.thank-you {
  font-size: 1.2rem;
  font-weight: 600;
  color: #00bcd4;
}

.read-more-btn {
  text-transform: none;
  font-weight: 600;
  font-size: 1rem;
  margin: 1.5rem 0;
}

.cta-section {
  background: #f8fdfe;
  padding: 2.5rem;
  border-radius: 16px;
  text-align: center;
  margin-top: 2.5rem;
  border: 1.5px solid #e0f7fa;
}

.cta-section h3 {
  font-size: 1.6rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 0.5rem;
}

.cta-section p {
  font-size: 1rem;
  color: #64748b;
  margin-bottom: 1.75rem;
}

.cta-buttons {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
}

.cta-buttons .v-btn {
  text-transform: none;
  font-weight: 600;
}

@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 960px) {
  .text-column {
    padding-left: 0;
    margin-top: 2rem;
  }

  .image-wrapper {
    position: relative;
    top: 0;
  }

  .profile-pic-about-me {
    max-width: 240px;
  }

  .cta-buttons {
    flex-direction: column;
    align-items: center;
  }

  .cta-buttons .v-btn {
    width: 100%;
    max-width: 300px;
  }
}
</style>
